/** i18next configuration — see docs/architecture.md, "i18n" section for the
 * resolution order (login-page pick > saved profile > localStorage > browser). */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import enCommon from './locales/en/common.json';
import enAuth from './locales/en/auth.json';
import enAds from './locales/en/ads.json';
import enCriteria from './locales/en/criteria.json';
import enWorkflow from './locales/en/workflow.json';
import enAdmin from './locales/en/admin.json';
import enScraping from './locales/en/scraping.json';
import frCommon from './locales/fr/common.json';
import frAuth from './locales/fr/auth.json';
import frAds from './locales/fr/ads.json';
import frCriteria from './locales/fr/criteria.json';
import frWorkflow from './locales/fr/workflow.json';
import frAdmin from './locales/fr/admin.json';
import frScraping from './locales/fr/scraping.json';
import roCommon from './locales/ro/common.json';
import roAuth from './locales/ro/auth.json';
import roAds from './locales/ro/ads.json';
import roCriteria from './locales/ro/criteria.json';
import roWorkflow from './locales/ro/workflow.json';
import roAdmin from './locales/ro/admin.json';
import roScraping from './locales/ro/scraping.json';

export const SUPPORTED_LANGUAGES = ['en', 'fr', 'ro'] as const;
export type Language = (typeof SUPPORTED_LANGUAGES)[number];
export const DEFAULT_LANGUAGE: Language = 'en';

/** Decorative only — see docs/architecture.md, "i18n" section. */
export const LANGUAGE_FLAGS: Record<Language, string> = {
  en: '🇬🇧',
  fr: '🇫🇷',
  ro: '🇷🇴',
};

// Fire-and-forget: init() resolves once resources are loaded, but nothing in
// this module needs to await it — consumers use i18n's own ready state.
void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: {
        common: enCommon,
        auth: enAuth,
        ads: enAds,
        criteria: enCriteria,
        workflow: enWorkflow,
        admin: enAdmin,
        scraping: enScraping,
      },
      fr: {
        common: frCommon,
        auth: frAuth,
        ads: frAds,
        criteria: frCriteria,
        workflow: frWorkflow,
        admin: frAdmin,
        scraping: frScraping,
      },
      ro: {
        common: roCommon,
        auth: roAuth,
        ads: roAds,
        criteria: roCriteria,
        workflow: roWorkflow,
        admin: roAdmin,
        scraping: roScraping,
      },
    },
    ns: ['common', 'auth', 'ads', 'criteria', 'workflow', 'admin', 'scraping'],
    defaultNS: 'common',
    supportedLngs: SUPPORTED_LANGUAGES,
    fallbackLng: DEFAULT_LANGUAGE,
    nonExplicitSupportedLngs: true,
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },
    interpolation: {
      escapeValue: false, // React already escapes
    },
    react: {
      useSuspense: false,
    },
  });

// Keep <html lang="..."> in sync for accessibility/SEO.
if (typeof document !== 'undefined') {
  document.documentElement.lang = i18n.language || DEFAULT_LANGUAGE;
  i18n.on('languageChanged', (lng) => {
    document.documentElement.lang = lng;
  });
}

export default i18n;
